import Vue from "vue"
import Router, {RawLocation, Route, RouteConfig} from "vue-router"
import routerGuardAuthentication from "./routerGuardAuthentication"
import routerGuardStatus from "@/plugins/routerGuardStatus"
import routerGuardOrganization from "@/plugins/routerGuardOrganization"
import routerGuardLocation from "@/plugins/routerGuardLocation"
import Navigation from "@/managers/session/navigation"
import Preferences from "@/managers/system/preferences"
import routerGuardUpdateAvailable from "@/plugins/routerGuardUpdateAvailable"
import MainMargins from "@/managers/system/mainMargins"
import Authentication from "@/managers/authentication/authentication"
import routerGuardPublishers from "@/plugins/routerGuardPublishers"
import {recoverFromChunkLoadError} from "@/utility/chunkLoadError"

const Login = () => import(/* webpackChunkName: "login" */ "@/views/login.vue")
const ResetPassword = () => import(/* webpackChunkName: "login" */ "@/views/resetPassword.vue")
const CreatePasskey = () => import(/* webpackChunkName: "login" */ "@/views/createPasskey.vue")

const NotFoundView = () => import(/* webpackChunkName: "common" */ "@/views/notFound.vue")
const TermsView = () => import(/* webpackChunkName: "common" */ "@/views/terms.vue")
const PrivacyView = () => import(/* webpackChunkName: "common" */ "@/views/privacy.vue")
const DataDeleted = () => import(/* webpackChunkName: "common" */ "@/views/dataDeleted.vue")

const SettingsUserProfile = () => import(/* webpackChunkName: "commonAccount" */ "@/views/userAccount/user/profile.vue")
const SettingsUserPasswordLogin = () => import(/* webpackChunkName: "commonAccount" */ "@/views/userAccount/user/passwordLogin.vue")
const SettingsUserNotifications = () => import(/* webpackChunkName: "commonAccount" */ "@/views/userAccount/user/notifications.vue")
const SettingsHelpSupport = () => import(/* webpackChunkName: "commonAccount" */ "@/views/userAccount/help/support.vue")
const SettingsHelpTerms = () => import(/* webpackChunkName: "commonAccount" */ "@/views/userAccount/help/terms.vue")
const SettingsHelpPrivacy = () => import(/* webpackChunkName: "commonAccount" */ "@/views/userAccount/help/privacy.vue")

const User = () => import(/* webpackChunkName: "user" */ "@/views/userAccount/user.vue")
const UserAccountView = () => import(/* webpackChunkName: "user" */ "@/views/userAccount/account.vue")
const UserDelete = () => import(/* webpackChunkName: "user" */ "@/views/userAccount/delete.vue")

const JoinOrganization = () => import(/* webpackChunkName: "invitation" */ "@/views/invitation/joinOrganization/joinOrganization.vue")
const SubscribeToOrganization = () => import(/* webpackChunkName: "invitation" */ "@/views/invitation/subscribeToOrganization/subscribeToOrganization.vue")
const JoinLocation = () => import(/* webpackChunkName: "invitation" */ "@/views/invitation/joinLocation/joinLocation.vue")
const CreateLocation = () => import(/* webpackChunkName: "invitation" */ "@/views/invitation/createLocation/createLocation.vue")

const SignUp = () => import(/* webpackChunkName: "signUp" */ "@/views/signUp/signUp.vue")
const SignUpUserSelection = () => import(/* webpackChunkName: "signUp" */ "@/views/signUp/userSelection.vue")
const SignUpCreateOrganization = () => import(/* webpackChunkName: "signUp" */ "@/views/signUp/createOrganization.vue")
const SignUpSelectModules = () => import(/* webpackChunkName: "signUp" */ "@/views/signUp/selectModules.vue")
const SignUpConnectPlatforms = () => import(/* webpackChunkName: "signUp" */ "@/views/signUp/connectPlatforms.vue")

const Location = () => import(/* webpackChunkName: "location" */ "@/views/user/user.vue")
const LocationSelection = () => import(/* webpackChunkName: "location" */ "@/views/user/locationSelection/locationSelection.vue")
const LocationCalendar = () => import(/* webpackChunkName: "location" */ "@/views/user/calendar/calendar.vue")
const LocationCalendarGrid = () => import(/* webpackChunkName: "location" */ "@/views/user/calendarGrid/calendarGrid.vue")
const LocationCalendarMonth = () => import(/* webpackChunkName: "location" */ "@/views/user/calendarMonth/calendarMonth.vue")
const LocationPostEditor = () => import(/* webpackChunkName: "location" */ "@/views/user/postEditor/postEditor.vue")
const LocationContent = () => import(/* webpackChunkName: "location" */ "@/views/user/content/content.vue")
const LocationDeals = () => import(/* webpackChunkName: "location" */ "@/views/user/deals/deals.vue")
const LocationDealsOrders = () => import(/* webpackChunkName: "location" */ "@/views/user/orders/orders.vue")
const LocationDealsDisabled = () => import(/* webpackChunkName: "location" */ "@/views/user/dealsDisabled/dealsDisabled.vue")
const LocationInsights = () => import(/* webpackChunkName: "location" */ "@/views/user/insights/insights.vue")

const LocationAccount = () => import(/* webpackChunkName: "locationAccount" */ "@/views/user/account/account.vue")
const LocationAccountContent = () => import(/* webpackChunkName: "locationAccount" */ "@/views/user/account/location/content.vue")
const LocationAccountTeam = () => import(/* webpackChunkName: "locationAccount" */ "@/views/user/account/location/team.vue")
const LocationAccountBilling = () => import(/* webpackChunkName: "locationAccount" */ "@/views/user/account/location/billing.vue")
const LocationAccountSales = () => import(/* webpackChunkName: "locationAccount" */ "@/views/user/account/location/sales/sales.vue")
const LocationAccountSalesPostTexts = () => import(/* webpackChunkName: "locationAccount" */ "@/views/user/account/location/sales/postTexts.vue")
const LocationAccountSalesReplyTexts = () => import(/* webpackChunkName: "locationAccount" */ "@/views/user/account/location/sales/replyTexts.vue")
const LocationAccountSalesSubscribers = () => import(/* webpackChunkName: "locationAccount" */ "@/views/user/account/location/sales/subscribers/subscribers.vue")
const LocationAccountSalesSubscribersMessages = () => import(/* webpackChunkName: "locationAccount" */ "@/views/user/account/location/sales/subscribers/messages.vue")

const Organization = () => import(/* webpackChunkName: "organization" */ "@/views/admin/admin.vue")
const OrganizationSelection = () => import(/* webpackChunkName: "organization" */ "@/views/admin/organizationSelection/organizationSelection.vue")
const OrganizationAccount = () => import(/* webpackChunkName: "organization" */ "@/views/admin/account/account.vue")
const OrganizationCalendar = () => import(/* webpackChunkName: "organization" */ "@/views/admin/calendar/calendar.vue")
const OrganizationCalendarGrid = () => import(/* webpackChunkName: "organization" */ "@/views/admin/calendarGrid/calendarGrid.vue")
const OrganizationCalendarMonth = () => import(/* webpackChunkName: "organization" */ "@/views/admin/calendarMonth/calendarMonth.vue")
const OrganizationScheduledTemplateEditor = () => import(/* webpackChunkName: "organization" */ "@/views/admin/scheduledTemplateEditor/scheduledTemplateEditor.vue")
const OrganizationContent = () => import(/* webpackChunkName: "organization" */ "@/views/admin/content/content.vue")
const OrganizationDeals = () => import(/* webpackChunkName: "organization" */ "@/views/admin/deals/deals.vue")
const OrganizationDealsReplyTexts = () => import(/* webpackChunkName: "organization" */ "@/views/admin/deals/organizationDealsReplyTexts.vue")
const OrganizationDealsPostsTexts = () => import(/* webpackChunkName: "organization" */ "@/views/admin/deals/organizationDealsPostTexts.vue")
const OrganizationDealsDisabled = () => import(/* webpackChunkName: "organization" */ "@/views/admin/dealsDisabled/dealsDisabled.vue")
const OrganizationEditor = () => import(/* webpackChunkName: "organization" */ "@/views/admin/editor/editor.vue")
const OrganizationInsights = () => import(/* webpackChunkName: "organization" */ "@/views/admin/insights/insights.vue")
const OrganizationLocations = () => import(/* webpackChunkName: "organization" */ "@/views/admin/locations/locations.vue")
const OrganizationBrandLocations = () => import(/* webpackChunkName: "organization" */ "@/views/admin/brandLocations/brandLocations.vue")
const OrganizationUsers = () => import(/* webpackChunkName: "organization" */ "@/views/admin/users/users.vue")
const OrganizationGroups = () => import(/* webpackChunkName: "organization" */ "@/views/admin/groups/groups.vue")
const OrganizationTemplates = () => import(/* webpackChunkName: "organization" */ "@/views/admin/templates/templates.vue")
const OrganizationTemplatesPostEditor = () => import(/* webpackChunkName: "organization" */ "@/views/admin/templates/templateEditor.vue")
const OrganizationReview = () => import(/* webpackChunkName: "organization" */ "@/views/admin/review/review.vue")
const OrganizationReviewPostEditor = () => import(/* webpackChunkName: "organization" */ "@/views/admin/review/postEditor.vue")

const Backoffice = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/backoffice.vue")
const BackofficeDashboard = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/dashboard/dashboard.vue")
const BackofficeLogs = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/logs/logs.vue")
const BackofficeLogsPostErrors = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/logs/postErrorLog.vue")
const BackofficeLogsFrontendErrors = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/logs/frontendErrorLog.vue")
const BackofficeLogsServiceErrors = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/logs/serviceErrorLog.vue")
const BackofficeOrganizations = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/organizations/organizations.vue")
const BackofficeOrganizationLocations = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/organizationLocations/organizationLocations.vue")
const BackofficeOrganizationUsers = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/organizationUsers/organizationUsers.vue")
const BackofficeUsers = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/users/users.vue")
const BackofficeServiceConnections = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/serviceConnections/serviceConnections.vue")
const BackofficeSettings = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/settings/settings.vue")
const BackofficeSettingsPostSuggestions = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/settings/postSuggestionsSettings.vue")
const BackofficeSettingsAppConfiguration = () => import(/* webpackChunkName: "backoffice" */ "@/views/backoffice/settings/appConfiguration.vue")

const Debug = () => import(/* webpackChunkName: "debug" */ "@/views/debug/debug.vue")

Vue.use(Router)

const router = new Router({
	mode: "history",
	base: process.env.BASE_URL,
	scrollBehavior: (to, from, savedPosition) => {
		if (savedPosition)
			return savedPosition

		if(to.hash)
			return {selector: to.hash, behavior: "smooth", offset: {x: 0, y: MainMargins.top}}

		const y = Navigation.scrollPosition ?? 0

		if (y !== 0)
			return {x: 0, y, behavior: "smooth"}

		return {x: 0, y: 0}
	},
	routes: handleChunkErrorInRoutes([
		{
			path: "/",
			beforeEnter: async (to: Route, from: Route, next: (to?: RawLocation | false | ((vm: Vue) => void)) => void) => {
				const isAuthenticated = await Authentication.checkLogin()

				if (isAuthenticated) {
					const key = Preferences.preferredLocation
					next(
						key !== null
							? {path: `/${key}`, replace: true}
							: {name: "locationSelection", replace: true}
					)
				} else
					next({name: "login", replace: true})
			}
		},
		{
			path: "/brand/:id(\\d+)/:subPage?",
			redirect: {name: "locationSelection", replace: true}
		},
		{
			path: "/admin/:id/feed",
			redirect: {name: "organizationSelection", replace: true}
		},
		{
			name: "login",
			path: "/login",
			component: Login,
			meta: {
				i18n: "login",
				requiresGuest: true,
				isUserBusy: true
			}
		},
		{
			name: "resetPassword",
			path: "/resetpassword",
			component: ResetPassword,
			props: route => ({ticketId: route.query.ticket}),
			meta: {
				i18n: "resetPassword",
				isUserBusy: true
			}
		},
		{
			name: "createPasskey",
			path: "/create-passkey",
			component: CreatePasskey,
			props: route => ({ticketId: route.query.ticket}),
			meta: {
				i18n: "createPasskey",
				isUserBusy: true
			}
		},
		{
			name: "joinOrganization",
			path: "/join",
			component: JoinOrganization,
			props: route => ({ticketId: route.query.ticket}),
			meta: {
				i18n: "invitation.joinOrganization",
				isUserBusy: true
			}
		},
		{
			name: "subscribeToOrganization",
			path: "/subscribecontent",
			component: SubscribeToOrganization,
			props: route => ({ticketId: route.query.ticket}),
			meta: {
				i18n: "invitation.subscribeToOrganization",
				isUserBusy: true
			}
		},
		{
			name: "joinLocation",
			path: "/joinlocation",
			component: JoinLocation,
			props: route => ({ticketId: route.query.ticket}),
			meta: {
				i18n: "invitation.joinLocation",
				isUserBusy: true
			}
		},
		{
			name: "createLocation",
			path: "/createlocation",
			component: CreateLocation,
			props: route => ({ticketId: route.query.ticket}),
			meta: {
				i18n: "invitation.createLocation",
				isUserBusy: true
			}
		},
		{
			name: "privacy",
			path: "/privacy",
			component: PrivacyView,
			meta: {
				i18n: "privacy",
				isUserBusy: true
			}
		},
		{
			name: "terms",
			path: "/terms",
			component: TermsView,
			meta: {
				i18n: "terms",
				isUserBusy: true
			}
		},
		{
			name: "dataDeleted",
			path: "/data-deleted",
			component: DataDeleted,
			meta: {
				i18n: "dataDeleted",
				isUserBusy: true
			}
		},
		{
			name: "debug",
			path: "/debug",
			component: Debug,
			meta: {
				i18n: "debug"
			}
		},
		{
			path: "/userdelete",
			redirect: {name: "userDelete", replace: true}
		},
		{
			path: "/user",
			component: User,
			meta: {
				i18n: "user",
				requiresAuthenticated: true
			},
			children: [
				{
					name: "userAccount",
					path: "/",
					component: UserAccountView,
					meta: {
						i18n: "account",
						isUserBusy: true
					}
				},
				... userProfilePages(),
				... helpPages(),
				{
					name: "userDelete",
					path: "delete",
					component: UserDelete,
					meta: {
						i18n: "delete",
						isUserBusy: true
					}
				}
			]
		},
		{
			path: "/signup",
			component: SignUp,
			meta: {
				i18n: "signup",
				isUserBusy: true
			},
			children: [
				{
					path: "",
					redirect: {name: "signUpUserSelection"}
				},
				{
					name: "signUpUserSelection",
					path: "user",
					component: SignUpUserSelection,
					meta: {
						i18n: "userSelection",
					}
				},
				{
					name: "signUpCreateOrganization",
					path: "organization",
					component: SignUpCreateOrganization,
					meta: {
						i18n: "createOrganization",
						requiresAuthenticated: true,
					}
				},
				{
					name: "signUpSelectModules",
					path: ":organizationRequestKey/modules",
					component: SignUpSelectModules,
					props: true,
					meta: {
						i18n: "selectModules",
						requiresAuthenticated: true,
					}
				},
				{
					name: "signUpConnectPlatforms",
					path: ":locationKey/connect",
					component: SignUpConnectPlatforms,
					meta: {
						i18n: "connectPlatforms",
						requiresAuthenticated: true,
					}
				}
			]
		},
		{
			path: "/backoffice",
			component: Backoffice,
			meta: {
				i18n: "backoffice",
				requiresAuthenticated: true,
				manageType: "backoffice",
				isUserBusy: true
			},
			children: [
				{
					path: "",
					component: BackofficeDashboard,
					meta: {
						i18n: "dashboard"
					}
				},
				{
					path: "logs",
					component: BackofficeLogs,
					meta: {
						i18n: "logs"
					},
					children: [
						{
							path: "",
							redirect: "posts",
						}, {
							name: "postErrorLog",
							path: "posts",
							component: BackofficeLogsPostErrors,
							meta: {
								i18n: "posts"
							}
						}, {
							name: "frontendErrorLog",
							path: "frontend",
							component: BackofficeLogsFrontendErrors,
							meta: {
								i18n: "frontend"
							}
						}, {
							name: "serviceErrorLog",
							path: "services",
							component: BackofficeLogsServiceErrors,
							meta: {
								i18n: "services"
							}
						}
					]
				},
				{
					path: "organizations",
					component: BackofficeOrganizations,
					meta: {
						i18n: "organizations"
					}
				},
				{
					path: "organizations/:organizationId/locations",
					component: BackofficeOrganizationLocations,
					props: true,
					meta: {
						i18n: "organizationLocations"
					}
				},
				{
					path: "organizations/:organizationId/users",
					component: BackofficeOrganizationUsers,
					props: true,
					meta: {
						i18n: "organizationUsers"
					}
				},
				{
					path: "users",
					component: BackofficeUsers,
					props: true,
					meta: {
						i18n: "users"
					}
				},
				{
					name: "serviceConnections",
					path: "serviceConnections/:serviceConnectionId?",
					component: BackofficeServiceConnections,
					props: true,
					meta: {
						i18n: "serviceConnections"
					}
				},
			{
				path: "settings",
				component: BackofficeSettings,
				props: true,
				meta: {
					i18n: "settings"
				},
				children: [
					{
						path: "",
						redirect: {name: "postSuggestionsSettings", replace: true},
					}, {
						name: "postSuggestionsSettings",
						path: "post-suggestions",
						component: BackofficeSettingsPostSuggestions,
						meta: {
							i18n: "postSuggestionsSettings"
						}
					}, {
						name: "appConfiguration",
						path: "app-configuration",
						component: BackofficeSettingsAppConfiguration,
						meta: {
							i18n: "appConfiguration"
						}
					}
				]
			}
			]
		},
		{
			name: "organizationSelection",
			path: "/admin",
			component: OrganizationSelection,
			meta: {
				i18n: "organization.organizationSelection",
				requiresAuthenticated: true,
				isUserBusy: true
			}
		},
		{
			path: "/admin/:organizationKey",
			component: Organization,
			meta: {
				i18n: "organization",
				requiresAuthenticated: true,
				requiresPublishers: true,
				manageType: "organization"
			},
			children: [
				{
					path: "",
					redirect: to => {
						const location = Preferences.calendarTypeRoute
						location.query = {... location.query, ... to.query as Record<string, string>}
						return location
					}
				}, {
					path: "account",
					component: OrganizationAccount,
					meta: {
						i18n: "account"
					}
				},
				... userProfilePages("account/user"),
				... helpPages("account"),
				{
					path: "calendar",
					component: OrganizationCalendar,
					meta: {
						i18n: "calendar"
					}
				}, {
					path: "calendar-grid",
					component: OrganizationCalendarGrid,
					meta: {
						i18n: "calendar"
					}
				}, {
					path: "calendar-month",
					component: OrganizationCalendarMonth,
					meta: {
						i18n: "calendar"
					}
				}, {
					path: "calendar/editor/:templateId?",
					alias: ["calendar-grid/editor/:templateId?", "calendar-month/editor/:templateId?"],
					component: OrganizationScheduledTemplateEditor,
					props: route => ({
						...route.params,
						publisherId: route.query.publisherId,
						postType: route.query.postType,
						publishDate: route.query.date,
						defaultDeal: "deal" in route.query
					}),
					meta: {
						i18n: "scheduledTemplateEditor"
					}
				}, {
					path: "content",
					redirect: "content/published"
				},{
					path: "content/:state",
					component: OrganizationContent,
					props: true,
					meta: {
						i18n: "content"
					}
				}, {
					path: "deals",
					component: OrganizationDeals,
					meta: {
						i18n: "deals"
					}
				}, {
					path: "deals-disabled",
					component: OrganizationDealsDisabled,
					meta: {
						i18n: "dealsDisabled"
					}
				}, {
					path: "deals/replies",
					component: OrganizationDealsReplyTexts,
					props: route => ({initialLanguage: route.query.language}),
					meta: {
						i18n: "deals.replies"
					}
				}, {
					path: "deals/posts",
					component: OrganizationDealsPostsTexts,
					props: route => ({initialLanguage: route.query.language}),
					meta: {
						i18n: "deals.posts"
					}
				}, {
					path: "review",
					component: OrganizationReview,
					meta: {
						i18n: "review"
					},
					children: [
						{
							path: "editor/:postId?",
							component: OrganizationReviewPostEditor,
							props: true,
							meta: {
								i18n: "postEditor"
							}
						}
					]
				}, {
					path: "editor",
					component: OrganizationEditor,
					meta: {
						i18n: "editor"
					}
				}, {
					path: "insights",
					component: OrganizationInsights,
					meta: {
						i18n: "insights"
					}
				}, {
					path: "locations",
					component: OrganizationLocations,
					meta: {
						i18n: "locations"
					}
				}
				, {
					path: "brand-locations",
					component: OrganizationBrandLocations,
					meta: {
						i18n: "brandLocations"
					}
				}, {
					path: "users",
					component: OrganizationUsers,
					meta: {
						i18n: "users"
					}
				}, {
					path: "groups",
					component: OrganizationGroups,
					meta: {
						i18n: "groups"
					}
				}, {
					path: "templates",
					component: OrganizationTemplates,
					meta: {
						i18n: "templates"
					},
					children: [
						{
							path: "editor/:templateId?",
							component: OrganizationTemplatesPostEditor,
							props: route => ({
								...route.params,
								categoryId: route.query.category,
								publisherId: route.query.publisherId,
								postType: route.query.postType,
								defaultDeal: "deal" in route.query
							}),
							meta: {
								i18n: "templateEditor"
							}
						}
					]
				}
			]
		},
		{
			name: "locationSelection",
			path: "/",
			component: LocationSelection,
			meta: {
				i18n: "location.locationSelection",
				requiresAuthenticated: true,
				isUserBusy: true
			}
		},
		{
			path: "/:locationKey",
			component: Location,
			meta: {
				i18n: "location",
				requiresAuthenticated: true,
				requiresPublishers: true,
				manageType: "location"
			},
			children: [
				{
					path: "",
					redirect: to => {
						const location = Preferences.calendarTypeRoute
						location.query = {... location.query, ... to.query as Record<string, string>}
						return location
					}
				}, {
					path: "calendar-month",
					component: LocationCalendarMonth,
					meta: {
						i18n: "calendar"
					}
				}, {
					path: "account",
					component: LocationAccount,
					meta: {
						i18n: "account"
					}
				}, {
					path: "account/location/content",
					component: LocationAccountContent,
					meta: {
						i18n: "account.locationSettings.content"
					}
				}, {
					path: "account/location/team",
					component: LocationAccountTeam,
					meta: {
						i18n: "account.locationSettings.team"
					}
				}, {
					path: "account/location/billing",
					component: LocationAccountBilling,
					meta: {
						i18n: "account.locationSettings.billing"
					}
				}, {
					path: "account/location/sales",
					component: LocationAccountSales,
					meta: {
						i18n: "account.locationSettings.sales"
					}
				}, {
					path: "account/location/sales/postTexts",
					component: LocationAccountSalesPostTexts,
					meta: {
						i18n: "account.locationSettings.sales.postTexts"
					}
				}, {
					path: "account/location/sales/replyTexts",
					component: LocationAccountSalesReplyTexts,
					meta: {
						i18n: "account.locationSettings.sales.replyTexts"
					}
				}, {
					path: "account/location/sales/subscribers",
					component: LocationAccountSalesSubscribers,
					meta: {
						i18n: "account.locationSettings.sales.subscribers"
					}
				}, {
					name: "locationSubscribeMessages",
					path: "account/location/sales/subscribers/messages/:category?",
					component: LocationAccountSalesSubscribersMessages,
					props: true,
					meta: {
						i18n: "account.locationSettings.sales.subscribers.messages"
					}
				},
				... userProfilePages("account/user"),
				... helpPages("account"),
				{
					path: "calendar",
					component: LocationCalendar,
					meta: {
						i18n: "calendar"
					}
				}, {
					path: "calendar-grid",
					component: LocationCalendarGrid,
					meta: {
						i18n: "calendar"
					},
				}, {
					path: "calendar/orders/:postId",
					alias: ["calendar-grid/orders/:postId", "calendar-month/orders/:postId", "deals/orders/:postId"],
					component: LocationDealsOrders,
					props: true,
					meta: {
						i18n: "deals.orders"
					}
				}, {
					path: "calendar/editor/:postId?",
					alias: ["calendar-grid/editor/:postId?", "calendar-month/editor/:postId?", "deals/editor/:postId?"],
					component: LocationPostEditor,
					props: route => ({
						...route.params,
						publisherId: route.query.publisherId,
						postType: route.query.postType,
						publishDate: route.query.date,
						defaultDeal: "deal" in route.query
					}),
					meta: {
						i18n: "postEditor"
					}
				}, {
					path: "deals/:category?",
					props: true,
					component: LocationDeals,
					meta: {
						i18n: "deals"
					}
				}, {
					path: "deals-disabled",
					component: LocationDealsDisabled,
					meta: {
						i18n: "dealsDisabled"
					}
				}, {
					path: "content",
					component: LocationContent,
					meta: {
						i18n: "content"
					}
				}, {
					path: "insights",
					component: LocationInsights,
					meta: {
						i18n: "insights"
					}
				}
			]
		},
		{
			path: "*",
			name: "notFound",
			component: NotFoundView,
			meta: {
				i18n: "notFound",
				isUserBusy: true
			}
		}
	])
})

function handleChunkErrorInRoutes(routes: RouteConfig[]): RouteConfig[] {
	const mutableRoutes = routes as {component?: unknown, children?: RouteConfig[]}[]
	for (const route of mutableRoutes) {
		if (typeof route.component === "function")
			route.component = handleChunkErrorInImport(route.component as () => Promise<unknown>)

		if (route.children !== undefined)
			handleChunkErrorInRoutes(route.children)
	}
	return routes
}

function handleChunkErrorInImport<T>(loader: () => Promise<T>): () => Promise<T> {
	return () => loader().catch(error => {
		if (recoverFromChunkLoadError(error))
			return new Promise<T>(() => { }) // Page is reloading; keep the navigation pending
		throw error
	})
}

function userProfilePages(prefixPath?: string): RouteConfig[] {
	if (prefixPath !== undefined)
		prefixPath += "/"
	else
		prefixPath = ""

	return [
		{
			path: prefixPath + "profile",
			component: SettingsUserProfile,
			meta: {
				i18n: "account.user.profile"
			}
		}, {
			path: prefixPath + "passwordLogin",
			component: SettingsUserPasswordLogin,
			meta: {
				i18n: "account.user.passwordLogin"
			}
		}, {
			path: prefixPath + "notifications",
			component: SettingsUserNotifications,
			meta: {
				i18n: "account.user.notifications"
			}
		}
	]
}

function helpPages(prefixPath?: string): RouteConfig[] {
	if (prefixPath !== undefined)
		prefixPath += "/"
	else
		prefixPath = ""

	return [
		{
			path: prefixPath + "help/support",
			component: SettingsHelpSupport,
			meta: {
				i18n: "account.help.support"
			}
		}, {
			path: prefixPath + "help/terms",
			component: SettingsHelpTerms,
			meta: {
				i18n: "account.help.terms"
			}
		}, {
			path: prefixPath + "help/privacy",
			component: SettingsHelpPrivacy,
			meta: {
				i18n: "account.help.privacy"
			}
		}
	]
}

routerGuardUpdateAvailable(router)
routerGuardStatus(router)
routerGuardAuthentication(router)
routerGuardOrganization(router)
routerGuardLocation(router)
routerGuardPublishers(router)

export default router
